import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { Row, Col, Button } from 'react-bootstrap';
import Select from 'react-select';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import { X } from "react-feather";
import { getFirmsList } from '../../../actions/firms';
import { getMachineOptions } from '../../../actions/machine';
import { getTransportOptions } from '../../../actions/transport';
import { useHistory, useLocation } from 'react-router-dom';

const ExpenseFilters = ({ filters, onFilterChange }) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const location = useLocation();
    const { expense_data } = useSelector(state => state.sparepart_expense);
    const { firms_list } = useSelector(state => state.firm);
    const { machine_options } = useSelector(state => state.machine);
    const { transport_options } = useSelector(state => state.transport);
    
    const [localFilters, setLocalFilters] = useState({
        firm_id: filters.firm_id || '',
        spare_part_id: filters.spare_part_id || '',
        warehouse_id: filters.warehouse_id || '',
        consumed_by: filters.consumed_by || '',
        machine_id: filters.machine_id || '',
        transport_id: filters.transport_id || '',
        date_from: filters.date_from || '',
        date_to: filters.date_to || ''
    });

    // Date range state
    const [dateRange, setDateRange] = useState([
        filters.date_from ? moment(filters.date_from).toDate() : null,
        filters.date_to ? moment(filters.date_to).toDate() : null
    ]);

    const [selectedFirm, setSelectedFirm] = useState(null);
    const [selectedSparePart, setSelectedSparePart] = useState(null);
    const [selectedWarehouse, setSelectedWarehouse] = useState(null);
    const [selectedConsumedBy, setSelectedConsumedBy] = useState(null);
    const [selectedMachine, setSelectedMachine] = useState(null);
    const [selectedTransport, setSelectedTransport] = useState(null);
    const [isUserInteracting, setIsUserInteracting] = useState(false);

    // Load options on component mount - run only once to avoid infinite loop
    useEffect(() => {
        if (!firms_list || firms_list.length === 0) {
            dispatch(getFirmsList());
        }
        if (!machine_options || machine_options.length === 0) {
            dispatch(getMachineOptions());
        }
        if (!transport_options || transport_options.length === 0) {
            dispatch(getTransportOptions());
        }
    }, []); // Run only on mount to avoid infinite loop

    // Read filters from URL on initial load only
    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        const urlFilters = {};
        
        ['firm_id', 'warehouse_id', 'spare_part_id', 'consumed_by', 'machine_id', 'transport_id', 'date_from', 'date_to'].forEach(key => {
            const value = urlParams.get(key);
            if (value) {
                urlFilters[key] = value;
            }
        });
        
        if (Object.keys(urlFilters).length > 0) {
            setLocalFilters(prev => ({ ...prev, ...urlFilters }));
            onFilterChange(urlFilters);
        }
    }, []); // Empty dependency array - run only on mount

    // Memoize options to prevent unnecessary re-renders
    const firmOptions = useMemo(() => {
        return firms_list?.map(firm => ({
            value: firm.id,
            label: firm.name
        })) || [];
    }, [firms_list]);

    const sparePartOptions = useMemo(() => {
        let spareParts = expense_data.spare_parts || [];
        
        // Filter spare parts by selected warehouse
        if (localFilters.warehouse_id) {
            spareParts = spareParts.filter(part => 
                part.warehouse_id === parseInt(localFilters.warehouse_id)
            );
        }
        // If no warehouse but firm is selected, filter by firm
        else if (localFilters.firm_id) {
            spareParts = spareParts.filter(part => 
                part.warehouse?.firm_id === parseInt(localFilters.firm_id)
            );
        }
        
        return spareParts.map(part => ({
            value: part.id,
            label: `${part.name} - ${part.warehouse?.name || ''}`
        }));
    }, [expense_data.spare_parts, localFilters.warehouse_id, localFilters.firm_id]);

    const warehouseOptions = useMemo(() => {
        let warehouses = expense_data.warehouses || [];
        
        // Filter warehouses by selected firm
        if (localFilters.firm_id) {
            warehouses = warehouses.filter(warehouse => 
                warehouse.firm_id === parseInt(localFilters.firm_id)
            );
        }
        
        return warehouses.map(warehouse => ({
            value: warehouse.id,
            label: warehouse.name
        }));
    }, [expense_data.warehouses, localFilters.firm_id]);

    const userOptions = useMemo(() => {
        return expense_data.users?.map(user => ({
            value: user.id,
            label: user.name
        })) || [];
    }, [expense_data.users]);

    const machineOptions = useMemo(() => {
        let machines = expense_data.machines || machine_options || [];
        
        // Filter machines by selected firm
        if (localFilters.firm_id) {
            machines = machines.filter(machine => 
                machine.firm_id === parseInt(localFilters.firm_id)
            );
        }
        
        return machines.map(machine => ({
            value: machine.id,
            label: machine.name
        }));
    }, [expense_data.machines, machine_options, localFilters.firm_id]);

    const transportOptions = useMemo(() => {
        let transports = expense_data.transports || transport_options || [];
        
        // Show all transports (not filtered by firm)
        return transports.map(transport => ({
            value: transport.id,
            label: `${transport.name}${transport.plate ? ` - ${transport.plate}` : ''}`
        }));
    }, [expense_data.transports, transport_options]);

    // Initialize selected values when options or filters change
    useEffect(() => {
        // Don't override user selections during interaction
        if (isUserInteracting) {
            setIsUserInteracting(false);
            return;
        }
        
        if (filters.firm_id && firmOptions.length > 0) {
            const firm = firmOptions.find(f => f.value === parseInt(filters.firm_id));
            if (firm) {
                setSelectedFirm(firm);
            }
        } else {
            setSelectedFirm(null);
        }

        if (filters.spare_part_id && sparePartOptions.length > 0) {
            const sparePart = sparePartOptions.find(p => p.value === parseInt(filters.spare_part_id));
            if (sparePart) {
                setSelectedSparePart(sparePart);
            } else {
                // If spare part not found in filtered options (due to warehouse/firm change), clear it
                setSelectedSparePart(null);
            }
        } else {
            setSelectedSparePart(null);
        }
        
        if (filters.warehouse_id && warehouseOptions.length > 0) {
            const warehouse = warehouseOptions.find(w => w.value === parseInt(filters.warehouse_id));
            if (warehouse) {
                setSelectedWarehouse(warehouse);
            } else {
                // If warehouse not found in filtered options (due to firm change), clear it
                setSelectedWarehouse(null);
            }
        } else {
            setSelectedWarehouse(null);
        }
        
        if (filters.consumed_by && userOptions.length > 0) {
            const user = userOptions.find(u => u.value === parseInt(filters.consumed_by));
            if (user) {
                setSelectedConsumedBy(user);
            }
        } else {
            setSelectedConsumedBy(null);
        }

        if (filters.machine_id && machineOptions.length > 0) {
            const machine = machineOptions.find(m => m.value === parseInt(filters.machine_id));
            if (machine) {
                setSelectedMachine(machine);
            } else {
                setSelectedMachine(null);
            }
        } else {
            setSelectedMachine(null);
        }

        if (filters.transport_id && transportOptions.length > 0) {
            const transport = transportOptions.find(t => t.value === parseInt(filters.transport_id));
            if (transport) {
                setSelectedTransport(transport);
            } else {
                setSelectedTransport(null);
            }
        } else {
            setSelectedTransport(null);
        }
    }, [firmOptions, sparePartOptions, warehouseOptions, userOptions, filters.firm_id, filters.spare_part_id, filters.warehouse_id, filters.consumed_by, machineOptions, transportOptions, filters.machine_id, filters.transport_id]);

    // Update date range when filters change from parent
    useEffect(() => {
        setDateRange([
            filters.date_from ? moment(filters.date_from).toDate() : null,
            filters.date_to ? moment(filters.date_to).toDate() : null
        ]);
    }, [filters.date_from, filters.date_to]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setLocalFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleDateRangeChange = (update) => {
        setDateRange(update);
        const [startDate, endDate] = update;
        
        const newFilters = {
            ...localFilters,
            date_from: startDate ? moment(startDate).format('YYYY-MM-DD') : '',
            date_to: endDate ? moment(endDate).format('YYYY-MM-DD') : ''
        };
        
        setLocalFilters(newFilters);
        updateURLWithFilters(newFilters);
        onFilterChange(newFilters);
    };

    const handleDateRangeReset = () => {
        setDateRange([null, null]);
        const newFilters = {
            ...localFilters,
            date_from: '',
            date_to: ''
        };
        setLocalFilters(newFilters);
        updateURLWithFilters(newFilters);
        onFilterChange(newFilters);
    };

    // Helper function to update URL with filters
    const updateURLWithFilters = (filters) => {
        const urlParams = new URLSearchParams();
        Object.entries(filters).forEach(([key, value]) => {
            if (value && value !== '') {
                urlParams.set(key, value);
            }
        });
        
        const newUrl = `${location.pathname}?${urlParams.toString()}`;
        history.push(newUrl);
    };

    const handleSelectChange = (name, selectedOption) => {
        const value = selectedOption?.value || '';
        setIsUserInteracting(true);

        // Update selected state and filters based on the field
        switch (name) {
            case 'firm_id':
                setSelectedFirm(selectedOption);
                // Clear dependent selections when firm changes (transport remains independent)
                setSelectedWarehouse(null);
                setSelectedSparePart(null);
                setSelectedMachine(null);
                const newFilters = {
                    ...localFilters,
                    firm_id: value,
                    warehouse_id: '',
                    spare_part_id: '',
                    machine_id: ''
                };
                setLocalFilters(newFilters);
                
                // Update URL immediately
                updateURLWithFilters(newFilters);
                // Apply filters immediately
                onFilterChange(newFilters);
                break;
            case 'spare_part_id':
                setSelectedSparePart(selectedOption);
                const sparePartFilters = {
                    ...localFilters,
                    spare_part_id: value
                };
                setLocalFilters(sparePartFilters);
                updateURLWithFilters(sparePartFilters);
                onFilterChange(sparePartFilters);
                break;
            case 'warehouse_id':
                setSelectedWarehouse(selectedOption);
                // Clear spare part selection when warehouse changes
                setSelectedSparePart(null);
                const warehouseFilters = {
                    ...localFilters,
                    warehouse_id: value,
                    spare_part_id: ''
                };
                setLocalFilters(warehouseFilters);
                updateURLWithFilters(warehouseFilters);
                onFilterChange(warehouseFilters);
                break;
            case 'consumed_by':
                setSelectedConsumedBy(selectedOption);
                const userFilters = {
                    ...localFilters,
                    consumed_by: value
                };
                setLocalFilters(userFilters);
                updateURLWithFilters(userFilters);
                onFilterChange(userFilters);
                break;
            case 'machine_id':
                setSelectedMachine(selectedOption);
                const machineFilters = {
                    ...localFilters,
                    machine_id: value
                };
                setLocalFilters(machineFilters);
                updateURLWithFilters(machineFilters);
                onFilterChange(machineFilters);
                break;
            case 'transport_id':
                setSelectedTransport(selectedOption);
                const transportFilters = {
                    ...localFilters,
                    transport_id: value
                };
                setLocalFilters(transportFilters);
                updateURLWithFilters(transportFilters);
                onFilterChange(transportFilters);
                break;
        }
    };



    const handleClearFilters = () => {
        const emptyFilters = {
            firm_id: '',
            spare_part_id: '',
            warehouse_id: '',
            consumed_by: '',
            machine_id: '',
            transport_id: '',
            date_from: '',
            date_to: ''
        };
        
        setLocalFilters(emptyFilters);
        setDateRange([null, null]);
        setSelectedFirm(null);
        setSelectedSparePart(null);
        setSelectedWarehouse(null);
        setSelectedConsumedBy(null);
        setSelectedMachine(null);
        setSelectedTransport(null);
        
        // Clear URL parameters using the helper function
        updateURLWithFilters(emptyFilters);
        
        // Pass the empty filters structure to parent
        onFilterChange(emptyFilters);
    };

    return (
        <div>
            <Row>
                {/* Column 1: Location/Organization */}
                <Col md={4}>
                    <Row>
                        <Col md={12} className="mb-2">
                            <Select
                                value={selectedFirm}
                                onChange={(option) => handleSelectChange('firm_id', option)}
                                options={firmOptions}
                                placeholder="Firmani tanlang..."
                                isClearable
                                isSearchable
                                className="form-select-sm"
                            />
                        </Col>
                        <Col md={12} className="mb-2">
                            <Select
                                value={selectedWarehouse}
                                onChange={(option) => handleSelectChange('warehouse_id', option)}
                                options={warehouseOptions}
                                placeholder={localFilters.firm_id ? "Omborni tanlang..." : "Avval firmani tanlang"}
                                isClearable
                                isDisabled={!localFilters.firm_id}
                                className="form-select-sm"
                            />
                        </Col>
                        <Col md={12} className="mb-2">
                            <Select
                                value={selectedSparePart}
                                onChange={(option) => handleSelectChange('spare_part_id', option)}
                                options={sparePartOptions}
                                placeholder={!localFilters.firm_id && !localFilters.warehouse_id ? "Avval firma/omborni tanlang" : "Ehtiyot qismni tanlang..."}
                                isClearable
                                isSearchable
                                isDisabled={!localFilters.firm_id && !localFilters.warehouse_id}
                                className="form-select-sm"
                            />
                        </Col>
                    </Row>
                </Col>

                {/* Column 2: Resources/Assets */}
                <Col md={4}>
                    <Row>
                        <Col md={12} className="mb-2">
                            <Select
                                value={selectedMachine}
                                onChange={(option) => handleSelectChange('machine_id', option)}
                                options={machineOptions}
                                placeholder={!localFilters.firm_id ? "Avval firmani tanlang" : "Uskunani tanlang..."}
                                isClearable
                                isSearchable
                                isDisabled={!localFilters.firm_id}
                                className="form-select-sm"
                            />
                        </Col>
                        <Col md={12} className="mb-2">
                            <Select
                                value={selectedTransport}
                                onChange={(option) => handleSelectChange('transport_id', option)}
                                options={transportOptions}
                                placeholder="Transportni tanlang..."
                                isClearable
                                isSearchable
                                className="form-select-sm"
                            />
                        </Col>
                        <Col md={12} className="mb-2">
                            <Select
                                value={selectedConsumedBy}
                                onChange={(option) => handleSelectChange('consumed_by', option)}
                                options={userOptions}
                                placeholder="Sarflagan shaxsni tanlang..."
                                isClearable
                                isSearchable
                                className="form-select-sm"
                            />
                        </Col>
                    </Row>
                </Col>

                {/* Column 3: Time/Actions */}
                <Col md={4}>
                    <Row>
                        <Col md={12} className="mb-2">
                            <div className="input-group input-group-sm">
                                <DatePicker
                                    wrapperClassName={'form-control form-control-sm'}
                                    className={'border-0 pe-0 w-100 focus-outline-0'}
                                    placeholderText={"Sana oralig'i: dd/mm/yyyy - dd/mm/yyyy"}
                                    selectsRange={true}
                                    startDate={dateRange[0]}
                                    endDate={dateRange[1]}
                                    onChange={handleDateRangeChange}
                                    dateFormat="dd/MM/yyyy"
                                    isClearable={false}
                                />
                                <span className="input-group-text cursor-pointer" onClick={handleDateRangeReset}>
                                    <X size={14} />
                                </span>
                            </div>
                        </Col>
                        <Col md={12} className="mb-2">
                            <Button 
                                variant="outline-secondary" 
                                size="sm" 
                                onClick={handleClearFilters}
                                className="w-100"
                            >
                                Barcha filtrlarni tozalash
                            </Button>
                        </Col>
                    </Row>
                </Col>
            </Row>
        </div>
    );
};

ExpenseFilters.propTypes = {
    filters: PropTypes.object.isRequired,
    onFilterChange: PropTypes.func.isRequired,
};

export default ExpenseFilters; 